/**
 * RequisitesViewModal - Модальное окно просмотра реквизитов
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Box,
  Button,
  CopyButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconStarFilled,
  IconCopy,
  IconCheck,
  IconPencil,
  IconBuildingBank,
  IconUser,
  IconMapPin,
  IconCalendar,
  IconPhone,
  IconMail,
  IconX,
  IconFileDescription,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Компонент для отображения поля с возможностью копирования
 */
const InfoField = ({ icon: Icon, label, value, copyable = true }) => {
  if (!value) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Group gap="xs">
          <Text size="sm">{value}</Text>
          {copyable && (
            <CopyButton value={value} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Скопировано!' : 'Копировать'} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? 'green' : 'gray'}
                    size="xs"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
        </Group>
      </Box>
    </Group>
  );
};

const RequisitesViewModal = ({
  opened,
  onClose,
  requisites,
  onEdit,
  onAddContract,
}) => {
  if (!requisites) return null;

  const isLegal = requisites.entity_type === 'legal';
  const isInactive = requisites.status === 'inactive';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Реквизиты"
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        {/* Заголовок с иконкой */}
        <Group>
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: isLegal ? '#e3f2fd' : '#fff3e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isLegal ? (
              <IconBuildingBank size={28} color="#1976d2" />
            ) : (
              <IconUser size={28} color="#e65100" />
            )}
          </Box>
          <Box style={{ flex: 1 }}>
            <Group gap="xs">
              <Text size="lg" fw={600}>
                {requisites.company_name}
              </Text>
              {requisites.is_primary && (
                <Badge
                  size="sm"
                  variant="filled"
                  radius="xl"
                  color="green"
                  leftSection={<IconStarFilled size={10} />}
                >
                  Основные
                </Badge>
              )}
              {isInactive && (
                <Badge
                  size="sm"
                  variant="filled"
                  radius="xl"
                  color="red"
                  leftSection={<IconX size={10} />}
                >
                  Не используются
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {requisites.entity_type_display}
            </Text>
            {requisites.edit_count > 0 && (
              <Badge size="xs" variant="light" radius="xl" color="blue" mt={4}>
                Изменено {requisites.edit_count} раз
              </Badge>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Основные реквизиты */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Основные реквизиты
          </Text>
          <InfoField
            icon={IconBuildingBank}
            label="ИНН"
            value={requisites.inn}
          />
          {requisites.kpp && (
            <InfoField
              icon={IconBuildingBank}
              label="КПП"
              value={requisites.kpp}
            />
          )}
          <InfoField
            icon={IconBuildingBank}
            label={isLegal ? 'ОГРН' : 'ОГРНИП'}
            value={requisites.ogrn}
          />
        </Stack>

        {/* Банковские реквизиты */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Банковские реквизиты
          </Text>
          <InfoField
            icon={IconBuildingBank}
            label="Расчётный счёт"
            value={requisites.bank_account}
          />
          <InfoField
            icon={IconBuildingBank}
            label="Банк"
            value={requisites.bank_name}
          />
          <InfoField
            icon={IconBuildingBank}
            label="БИК"
            value={requisites.bank_bik}
          />
        </Stack>

        {/* Адреса */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Адреса
          </Text>
          <InfoField
            icon={IconMapPin}
            label="Юридический адрес"
            value={requisites.legal_address}
          />
          <InfoField
            icon={IconMapPin}
            label="Физический адрес"
            value={requisites.physical_address}
          />
        </Stack>

        {/* Руководство и контакты */}
        {(requisites.director_name || requisites.contact_person_name) && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Руководство и контакты
            </Text>
            {requisites.director_name && (
              <InfoField
                icon={IconUser}
                label="Директор / Руководитель"
                value={requisites.director_name}
                copyable={false}
              />
            )}
            {requisites.contact_person_name && (
              <Box>
                <InfoField
                  icon={IconUser}
                  label="Контактное лицо"
                  value={requisites.contact_person_name}
                  copyable={false}
                />
                {requisites.contact_person_phone && (
                  <Group gap="xs" ml={40} mt={4}>
                    <IconPhone size={12} color={tokens.colors.neutral[6]} />
                    <Text size="xs" c="dimmed">
                      {requisites.contact_person_phone}
                    </Text>
                  </Group>
                )}
                {requisites.contact_person_email && (
                  <Group gap="xs" ml={40} mt={2}>
                    <IconMail size={12} color={tokens.colors.neutral[6]} />
                    <Text size="xs" c="dimmed">
                      {requisites.contact_person_email}
                    </Text>
                  </Group>
                )}
              </Box>
            )}
          </Stack>
        )}

        {/* Договор */}
        {(requisites.contract_start_date || requisites.class_days || requisites.class_time) && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Договор
            </Text>
            {requisites.contract_start_date && (
              <InfoField
                icon={IconCalendar}
                label="Дата начала договора"
                value={new Date(requisites.contract_start_date).toLocaleDateString('ru-RU')}
                copyable={false}
              />
            )}
            {requisites.class_days && (
              <InfoField
                icon={IconCalendar}
                label="Дни занятий"
                value={requisites.class_days}
                copyable={false}
              />
            )}
            {requisites.class_time && (
              <InfoField
                icon={IconCalendar}
                label="Время занятий"
                value={requisites.class_time}
                copyable={false}
              />
            )}
          </Stack>
        )}

        {/* Причина деактивации */}
        {isInactive && requisites.inactive_reason && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Причина деактивации
            </Text>
            <Box
              p="sm"
              style={{
                backgroundColor: '#fff5f5',
                borderRadius: '8px',
                border: '1px solid #ffcdd2',
              }}
            >
              <Text size="sm" c="red">
                {requisites.inactive_reason}
              </Text>
              {requisites.inactive_at && (
                <Text size="xs" c="dimmed" mt={4}>
                  {new Date(requisites.inactive_at).toLocaleString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Moscow',
                  })}
                </Text>
              )}
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          {onAddContract && (
            <Button
              variant="light"
              leftSection={<IconFileDescription size={16} />}
              onClick={() => {
                onClose();
                onAddContract(requisites);
              }}
            >
              Добавить договор
            </Button>
          )}
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => {
              onClose();
              onEdit(requisites);
            }}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RequisitesViewModal;
